import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { supabase } from '@/lib/supabase';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ErrorBubble } from '@/components/ui/ErrorBubble';
import { Turnstile } from '@marsidev/react-turnstile';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Logo } from '@/components/ui/Logo';
import { trackEvent } from '@/lib/gtm';
import { trackHubspotLogin } from '@/services/hubspotService';
import { SocialLoginButtons } from '@/components/ui/SocialLoginButtons';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAACr6Jt9kXRTg--6h';

const loginSchema = z.object({
    email: z.string().email({ message: "Por favor, insira um e-mail válido." }),
    password: z.string().min(1, { message: "A senha é obrigatória." })
});

type LoginFormData = z.infer<typeof loginSchema>;


interface LoginFormProps {
    onToggleView: () => void;
    isActive?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onToggleView, isActive = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const turnstileRef = useRef<any>(null);

    const [loginCaptchaToken, setLoginCaptchaToken] = useState<string>('');
    const [pendingLoginSubmit, setPendingLoginSubmit] = useState(false);
    const [loadingLogin, setLoadingLogin] = useState(false);
    const [generalError, setGeneralError] = useState<string>('');
    const [tsId] = useState(() => `ts-login-${Math.random().toString(36).slice(2, 9)}`);
    const [renderTs, setRenderTs] = useState(false);

    useEffect(() => {
        if (isActive) {
            const timer = setTimeout(() => setRenderTs(true), 700);
            return () => clearTimeout(timer);
        } else {
            setRenderTs(false);
        }
    }, [isActive]);

    const { register, handleSubmit, formState: { errors }, watch } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: ''
        }
    });

    // To retry submission if captcha was pending
    const formData = watch();

    const onSubmit = async (data: LoginFormData) => {
        if (!loginCaptchaToken) {
            setPendingLoginSubmit(true);
            return;
        }

        setPendingLoginSubmit(false);
        setLoadingLogin(true);
        setGeneralError('');

        try {
            const { data: authData, error } = await supabase.auth.signInWithPassword({
                email: data.email,
                password: data.password,
                options: {
                    captchaToken: loginCaptchaToken
                }
            });
            if (error) throw error;
            
            trackHubspotLogin({
                email: data.email,
                userId: authData.user?.id,
            });

            trackEvent('login');
            navigate('/');
        } catch (err: unknown) {
            setLoginCaptchaToken('');
            
            if (err instanceof Error) {
                if (err.message === 'Invalid login credentials') {
                    setGeneralError('E-mail ou senha incorretos.');
                } else {
                    setGeneralError('Ocorreu um erro ao fazer login. Tente novamente.');
                }
            } else {
                setGeneralError('Ocorreu um erro desconhecido.');
            }
        } finally {
            setLoadingLogin(false);
        }
    };

    useEffect(() => {
        if (pendingLoginSubmit && loginCaptchaToken) {
            setPendingLoginSubmit(false);
            // Re-trigger submit with current form data
            onSubmit(formData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingLoginSubmit, loginCaptchaToken]);

    return (
        <>
            <div className="w-1/2 p-4 sm:p-6 flex flex-col justify-center h-full">
                <Logo 
                    theme="light" 
                    size="large" 
                    className="mb-3 sm:mb-4 mt-2" 
                    showSubtitle 
                    subtitleText="Acesse sua suite de simulação." 
                />

            <form id="form-login-usuario" name="Login PersonaLab" onSubmit={handleSubmit(onSubmit)} className="space-y-2.5 sm:space-y-3">
                <SocialLoginButtons 
                    mode="login" 
                    disabled={loadingLogin || pendingLoginSubmit}
                    onError={setGeneralError}
                />

                <div className="relative">
                    {/* Conteúdo normal (mantém a altura do layout mesmo quando invisível) */}
                    <div className={`space-y-2.5 sm:space-y-3 transition-all duration-500 origin-center ${loadingLogin || pendingLoginSubmit ? 'opacity-0 invisible scale-95' : 'opacity-100 visible scale-100'}`}>
                        <div className="space-y-1.5 relative">
                            <label htmlFor="email" className="text-sm font-bold text-slate-700">E-mail</label>
                            <input id="email" type="email" autoComplete="email" disabled={loadingLogin || pendingLoginSubmit}
                                className={`w-full px-4 py-2 border bg-slate-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm disabled:opacity-70 disabled:cursor-not-allowed ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-200 focus:border-indigo-500'}`}
                                placeholder="voce@empresa.com"
                                {...register('email')}
                            />
                            <ErrorBubble message={errors.email?.message} />
                        </div>
                        <div className="space-y-1.5 relative">
                            <label htmlFor="password" className="text-sm font-bold text-slate-700">Senha</label>
                            <input id="password" type="password" autoComplete="current-password" disabled={loadingLogin || pendingLoginSubmit}
                                className={`w-full px-4 py-2 border bg-slate-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm disabled:opacity-70 disabled:cursor-not-allowed ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-200 focus:border-indigo-500'}`}
                                placeholder="••••••••"
                                {...register('password')}
                            />
                            <ErrorBubble message={errors.password?.message} />
                        </div>
                        
                        <div className="flex justify-end pt-1">
                            <Link 
                                to={`/esqueci-a-senha?email=${encodeURIComponent(watch('email') || '')}`} 
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                            >
                                Esqueci minha senha
                            </Link>
                        </div>
                        


                    </div>

                    {/* Portal de Loading */}
                    {(loadingLogin || pendingLoginSubmit) && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-sm z-10 animate-in fade-in zoom-in-95 duration-500 rounded-xl">
                            <div className="relative flex items-center justify-center w-16 h-16">
                                <div className="absolute inset-0 border-[4px] border-indigo-100 rounded-full"></div>
                                <div className="absolute inset-0 border-[4px] border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                            </div>
                            <p className="mt-5 text-[14px] font-bold text-indigo-700 animate-pulse transition-all">
                                {pendingLoginSubmit ? 'Validando segurança...' : 'Autenticando sessão...'}
                            </p>
                        </div>
                    )}
                </div>

                <Button 
                    type="submit" 
                    fullWidth 
                    loading={loadingLogin || pendingLoginSubmit} 
                    disabled={loadingLogin || pendingLoginSubmit} 
                    className="py-2.5 text-sm font-bold shadow-lg shadow-indigo-600/20 rounded-xl"
                >
                    {pendingLoginSubmit ? 'Verificando sistema...' : loadingLogin ? 'Conectando...' : 'Fazer Login'}
                </Button>

                <div className="relative w-full h-0">
                    {generalError && (
                        <div className="absolute top-1 left-0 w-full text-red-700 text-sm bg-red-50 p-2.5 rounded-xl border border-red-100 font-medium animate-in fade-in slide-in-from-top-1 text-center z-10 shadow-sm">
                            {generalError}
                        </div>
                    )}
                </div>

            </form>

            {createPortal(
                <div className="fixed bottom-4 right-4 z-[9999] transform scale-75 origin-bottom-right pointer-events-auto">
                    {renderTs && (
                        <Turnstile 
                            ref={turnstileRef}
                            key={location.key ? `login-ts-${location.key}` : tsId}
                            id={tsId}
                            options={{ size: 'flexible' }}
                            siteKey={TURNSTILE_SITE_KEY} 
                            onSuccess={(token) => {
                                setLoginCaptchaToken(token);
                            }}
                            onError={() => {
                                setPendingLoginSubmit(false);
                            }}
                        />
                    )}
                </div>,
                document.body
            )}

            <div className="mt-2 pt-3 border-t border-slate-100 flex flex-col items-center">
                <p className="text-sm text-slate-500 mb-2 sm:mb-3 font-medium">Não tem uma conta?</p>
                <button
                    type="button"
                    onClick={() => {
                        setGeneralError('');
                        onToggleView();
                    }}
                    className="group flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-bold transition-all duration-300 bg-transparent text-indigo-700 border-2 border-indigo-400 hover:border-indigo-600 hover:bg-indigo-50 hover:text-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed animate-glow-pulse"
                >
                    Cadastre-se grátis
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
            </div>
            </div>
        </>
    );
};
